import { colors, Icon, ICONS, SortableWidgetProps, TooltipPosition, Widget } from '@creditsights/styleguide-web';
import classNames from 'classnames/bind';
import React, { forwardRef, Fragment } from 'react';
import { useLocation } from 'react-router-dom';
import { EXTERNAL_ROUTES, ROUTES } from '../../../Base.constants';
import { useFeedFilters } from '../../../hooks/common';
import { useFetchRecentRecChanges } from '../../../hooks/issuers';
import { ViewChange } from '../../../models';
import { IssuerListDto } from '../../../services/dtos/issuer/issuerList.dto';
import { formatCommonDate } from '../../../utils/dateUtils';
import ExternalMfeLink from '../../MfeLink/ExternalMfeLink';
import CompanyTitle from '../common/CompanyTitle';
import InfoButton from '../common/InfoButton';
import styles from './RecentRecChangesWidget.module.scss';

const cx = classNames.bind(styles);

const renderRecommendationChange = (viewChange: ViewChange | undefined) => {
  switch (viewChange) {
    case ViewChange.New: {
      return (
        <div className={cx('new')} style={{ color: colors.changesNew }} data-testid="new">
          <span>N</span>
          <span>e</span>
          <span>w</span>
        </div>
      );
    }
    case ViewChange.Up: {
      return <Icon name={ICONS.ArrowUp} height={12} width={12} color={colors.changesUp} dataTestId="arrow-up-icon" />;
    }
    case ViewChange.Down: {
      return (
        <Icon name={ICONS.ArrowDown} height={12} width={12} color={colors.changesDown} dataTestId="arrow-down-icon" />
      );
    }
    default:
      return null;
  }
};

const renderRecentRecChanges = (recommendations: IssuerListDto | undefined) => {
  const isRecommendationChangeVisible = recommendations?.results.some(
    (recommendation) => recommendation.latestRec?.viewChange
  );
  return recommendations && recommendations?.results.length > 0 ? (
    <div className={cx('content')}>
      <div>
        {recommendations.results.map((recommendation) => (
          <Fragment key={recommendation.tagId}>
            <div className={cx('container')} data-testid="recent-rec-changes-items">
              <div className={cx('items')}>
                {isRecommendationChangeVisible && (
                  <div className={cx('icon')}>{renderRecommendationChange(recommendation.latestRec?.viewChange)}</div>
                )}
                <CompanyTitle
                  issuerTag={recommendation.tagId}
                  displayName={recommendation.displayName}
                  weighting={recommendation.latestRec?.weighting}
                  hasLink
                />
              </div>
              <div className={cx('date')} data-testid="recent-rec-changed-date">
                {formatCommonDate(recommendation.latestRec?.changedDate)}
              </div>
            </div>
          </Fragment>
        ))}
      </div>
      <ExternalMfeLink route={EXTERNAL_ROUTES.companyList} className={cx('link')}>
        View more
      </ExternalMfeLink>
    </div>
  ) : (
    <div>No Recent Rec Changes.</div>
  );
};

const RecentRecChangesWidget = (props: SortableWidgetProps, ref): React.ReactElement => {
  const { pathname } = useLocation();
  const { filters } = useFeedFilters();
  const {
    isLoading,
    data: recommendations,
    isRefetching,
    isError,
    refetch,
  } = useFetchRecentRecChanges(pathname === ROUTES.feed, filters);

  const header = (
    <div className={cx('heading')}>
      Recent Rec Changes
      <InfoButton tooltipPosition={TooltipPosition.TopCenter} />
    </div>
  );

  return (
    <div className={cx('body')}>
      <Widget
        headerTitle={header}
        isLoading={isLoading || isRefetching}
        isError={isError}
        onRetryAfterErrorClick={refetch}
        fixedHeight
        dataTestId="recent-rec-changes"
        ref={ref}
        {...props}
      >
        {filters ? renderRecentRecChanges(recommendations) : undefined}
      </Widget>
    </div>
  );
};

export default forwardRef(RecentRecChangesWidget);
